import { supabase } from '../lib/supabase';

export interface AIToolSuggestion {
  id: string;
  label: string;
  type: string;
  confidence: number;
  description: string;
}

export interface AIToolsResponse {
  suggestions: AIToolSuggestion[];
}

export interface Message {
  id: string;
  body: string;
  direction: 'inbound' | 'outbound';
  message_type: 'sms' | 'email';
  created_at: string;
  sender?: 'customer' | 'business';
}

/**
 * Get AI-powered tool suggestions based on recent messages
 */
/**
 * Fast keyword-based tool detection for instant UI response
 */
function getInstantToolSuggestions(messageBody: string): AIToolSuggestion[] {
  const suggestions: AIToolSuggestion[] = [];
  const messageText = messageBody.toLowerCase();
  
  // Invoice keywords - instant detection
  const invoiceKeywords = ['invoice', 'receipt', 'bill', 'payment', 'proof of payment', 'send me', 'can you send'];
  
  if (invoiceKeywords.some(keyword => messageText.includes(keyword))) {
    suggestions.push({
      id: 'instant_invoice',
      label: 'Send Invoice',
      type: 'send_invoice',
      confidence: 0.85,
      description: 'Invoice request detected'
    });
  }

  // Future: Add more instant detections here
  // - Schedule/reschedule keywords
  // - Quote/estimate keywords
  // - Status update keywords

  return suggestions;
}

export async function getAIToolSuggestions(
  businessId: string,
  contactInfo: string,
  messageType: 'sms' | 'email',
  customerId?: string
): Promise<AIToolSuggestion[]> {
  try {
    console.log('🤖 Getting AI tool suggestions for:', { businessId, contactInfo, messageType, customerId });

    // Get recent messages for this conversation
    const recentMessages = await getRecentMessages(businessId, contactInfo, messageType);
    console.log('🤖 Got recent messages:', recentMessages.length, 'messages');
    
    if (!recentMessages || recentMessages.length === 0) {
      console.log('📭 No recent messages found, returning empty suggestions');
      return [];
    }

    // Get the most recent customer message
    const customerMessages = recentMessages.filter(msg => 
      msg.sender === 'customer' || (!msg.sender && msg.direction === 'inbound')
    );

    if (customerMessages.length === 0) {
      console.log('📭 No customer messages found, returning empty suggestions');
      return [];
    }

    const latestMessage = customerMessages[customerMessages.length - 1];
    const messageBody = latestMessage.body || latestMessage.content || '';

    console.log('📨 Latest customer message:', messageBody);

    // ⚡ INSTANT: Return keyword-based suggestions immediately
    const instantSuggestions = getInstantToolSuggestions(messageBody);
    
    if (instantSuggestions.length > 0) {
      console.log('⚡ Instant suggestions found:', instantSuggestions);
      
      // Optional: Validate with AI in background (don't await)
      validateSuggestionsWithAI(businessId, messageBody, instantSuggestions).catch(error => {
        console.warn('Background AI validation failed:', error);
      });
      
      return instantSuggestions;
    }

    // If no instant matches, fall back to full AI analysis (slower but more accurate)
    console.log('🧠 No instant matches, using full AI analysis...');
    return await getFullAIAnalysis(businessId, messageBody);

  } catch (error) {
    console.error('❌ Error getting AI tool suggestions:', error);
    return [];
  }
}

/**
 * Background AI validation (optional, for future refinement)
 */
async function validateSuggestionsWithAI(businessId: string, messageBody: string, suggestions: AIToolSuggestion[]): Promise<void> {
  // This runs in background to validate/refine suggestions
  // Could be used to improve accuracy over time
  console.log('🔍 Background AI validation for:', suggestions.map(s => s.type));
  
  // Future: Call AI service to validate and potentially refine suggestions
  // For now, just log that we detected the keywords correctly
}

/**
 * Full AI analysis for complex cases
 */
async function getFullAIAnalysis(businessId: string, messageBody: string): Promise<AIToolSuggestion[]> {
  const aiServiceUrl = 'https://booking-app-production-d357.up.railway.app';

  console.log('🌐 Calling AI service for full analysis:', `${aiServiceUrl}/chat-with-functions`);

  const response = await fetch(`${aiServiceUrl}/chat-with-functions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      business_id: businessId,
      message: messageBody,
      speech_enabled: false,
      conversation_history: []
    })
  });

  console.log('📥 AI service response status:', response.status);

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ AI service error:', response.status, response.statusText, errorText);
    return [];
  }

  const data = await response.json();
  console.log('🎯 AI chat response received:', data);

  // Check if AI called the send_invoice function
  const suggestions: AIToolSuggestion[] = [];
  
  if (data.function_calls_made && data.function_calls_made.length > 0) {
    for (const functionCall of data.function_calls_made) {
      if (functionCall.function === 'send_invoice') {
        suggestions.push({
          id: 'ai_invoice',
          label: 'Send Invoice',
          type: 'send_invoice',
          confidence: 0.95,
          description: 'AI detected invoice request and can send it automatically'
        });
      }
    }
  }

  // Also check if AI response mentions invoice-related terms
  const responseText = data.response?.toLowerCase() || '';
  const invoiceKeywords = ['invoice', 'receipt', 'bill', 'payment'];
  
  if (invoiceKeywords.some(keyword => responseText.includes(keyword)) && suggestions.length === 0) {
    suggestions.push({
      id: 'detected_invoice',
      label: 'Send Invoice',
      type: 'send_invoice',
      confidence: 0.8,
      description: 'Invoice request detected in conversation'
    });
  }

  console.log('🎯 Generated suggestions:', suggestions);
  return suggestions;
}

/**
 * Get recent messages for the conversation
 */
async function getRecentMessages(
  businessId: string,
  contactInfo: string,
  messageType: 'sms' | 'email',
  limit: number = 10
): Promise<Message[]> {
  try {
    console.log('📞 Fetching messages for:', { businessId, contactInfo, messageType });

    // Use the exact same query logic as Messages page
    let messageQuery = supabase
      .from('all_messages')
      .select('*')
      .eq('business_id', businessId)
      .order('created_at', { ascending: false })
      .limit(limit);
    
    console.log('📞 Base query created for business:', businessId);

    // Apply filtering logic - but don't assume customer_id is null
    if (messageType === 'sms') {
      messageQuery = messageQuery.eq('message_type', 'sms')
                                 .or(`from_number.eq.${contactInfo},to_number.eq.${contactInfo}`);
    } else if (messageType === 'email') {
      messageQuery = messageQuery.eq('message_type', 'email')
                                 .or(`from_number.eq.${contactInfo},to_number.eq.${contactInfo}`);
    }

    const { data, error } = await messageQuery;

    if (error) {
      console.error('❌ Error fetching recent messages:', error);
      return [];
    }

    console.log('📨 Raw messages from DB:', data?.length || 0, 'messages');
    console.log('📨 Sample message:', data?.[0]);

    // Add sender attribution for AI processing and use the right body field
    const messagesWithSender = (data || []).map(msg => ({
      ...msg,
      // Use body field primarily, fallback to content if body is empty
      body: msg.body || msg.content || '',
      sender: msg.direction === 'inbound' ? 'customer' : 'business'
    }));

    return messagesWithSender.reverse(); // Return in chronological order

  } catch (error) {
    console.error('❌ Error in getRecentMessages:', error);
    return [];
  }
}
